/**
 * Web-side wrapper around the shared `liberty-core/featureFlags` client.
 *
 * Provides:
 *   - The gateway URL resolver (uses `REACT_APP_IS_QA` to pick staging vs
 *     prod).
 *   - A `localStorage`-backed persistence adapter so the previous session's
 *     flags are immediately available on page load.
 *
 * See `packages/liberty-core/featureFlags.ts` for the full data flow.
 */
import {createRemoteFeatureFlagClient} from "liberty-core/featureFlags";

// Whether this is a staging build. Same `REACT_APP_IS_QA` signal that
// `getMobileGatewayBaseUrl` uses to choose the staging vs prod gateway, so the
// gateway and the per-environment flag field (`enabledStaging`/`enabledProd`)
// can't drift.
function isStagingBuild(): boolean {
  return process.env.REACT_APP_IS_QA === "true";
}

function getMobileGatewayBaseUrl(): string {
  const host = isStagingBuild()
    ? "mobilegateway-staging.libertysoftware.net"
    : "mobilegateway.libertysoftware.net";
  return `https://${host}/api`;
}

const localStorageAdapter = {
  getItem: async (key: string) => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: async (key: string, value: string) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Ignore — quota exceeded, private mode, etc.
    }
  },
  removeItem: async (key: string) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Ignore.
    }
  },
};

// The API server version (returned by `me`) is unknown until sign-in, but the
// flag client needs it at refresh time. Auth code calls `setCurrentApiVersion`
// after each `me()` response and `clearCurrentApiVersion()` on sign-out.
let _currentApiVersion = "";

export function setCurrentApiVersion(version: string | null | undefined): void {
  _currentApiVersion = version ?? "";
}

export function clearCurrentApiVersion(): void {
  _currentApiVersion = "";
}

const client = createRemoteFeatureFlagClient({
  gatewayBaseUrl: getMobileGatewayBaseUrl,
  isStaging: isStagingBuild,
  getCurrentApiVersion: () => _currentApiVersion,
  storage: localStorageAdapter,
  log: (msg, err) => {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.warn(`[remoteFeatureFlags] ${msg}`, err);
    }
  },
});

export const refreshRemoteFeatureFlags = client.refresh;
export const loadCachedRemoteFeatureFlags = client.loadCached;
export const clearRemoteFeatureFlags = client.clear;
export const isRemoteFeatureEnabled = client.isEnabled;
export const getAllRemoteFeatureFlags = client.getAll;
export const getRemoteFeatureFlagStatus = client.getStatus;
export const subscribeRemoteFeatureFlagStatus = client.subscribeStatus;
export const useRemoteFeatureFlag = client.useFlag;
export const useRemoteFeatureFlagStatus = client.useStatus;
